import { Color } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OutsideClickHandler from 'react-outside-click-handler';
import Webcam from 'react-webcam';
import { COLORS, MEDIA_QUERIES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import defaultTheme from 'design-system/theme';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconCameraTorch from 'images/icons/inquiry/camera-torch.svg';
import SwitchCamera from 'images/inquiry/switch-camera.svg';
import Button from 'inquiry/components/Button';
import Dropdown from 'inquiry/components/Dropdown';
import {
  SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO,
  VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO,
} from 'inquiry/components/ScannerOverlay/ScannerOverlay';
import ScannerOverlayNew from 'inquiry/components/ScannerOverlay/ScannerOverlayNew';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import type {
  AutoCapturePillPassthroughProps,
  ScannerHelpModalPassthroughProps,
  ScannerOverlayPassthroughProps,
} from 'inquiry/lib/types';
import FrameTrigger, { FrameTriggerConfig } from 'lib/clients/FrameTrigger';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient, { FRAME_INTERVAL } from 'lib/clients/LivePhotoCaptureClient';
import { VideoClient } from 'lib/clients/VideoClient';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { useCameraInspection } from 'lib/hooks/useCameraInspection';
import useDebounced from 'lib/hooks/useDebounced';
import useInquiryFeatureFlag from 'lib/hooks/useInquiryFeatureFlag';
import useIsMounted from 'lib/hooks/useIsMounted';
import useToggle from 'lib/hooks/useToggle';
import useTrackVideoDimensions from 'lib/hooks/useTrackVideoDimensions';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { ButtonComponentStyles } from 'lib/interfaces/ui-components';
import { GovernmentIdOverlay, GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { captureException, captureMessage } from 'lib/reporting';
import { createCanvas } from 'lib/utils/canvas';
import { supportsInputCapture } from 'lib/utils/device';
import { isInCypress } from 'lib/utils/dom';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import ThemedSvg from 'verify/components/ThemedSvg';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import FlowType from 'verify/lib/interfaces/FlowType';
import { Rect } from 'verify/lib/utils/image';
import {
  bufferImageWithOverlayWindow,
  calculateViewfinderRegionInCanvas,
} from 'verify/lib/utils/webcam';
import { DocumentFileOriginal } from 'verify/models/document-file';
import { CaptureMethod, ClientExtraction } from 'verify/models/verification';
import { SelectedCamera } from 'verify/scanners/GovernmentIdMicroblinkScanner/MicroblinkCameraUtils';
import { Z_INDICES } from 'verify/styles/variables';

interface VideoWrapperProps {
  mirrored: boolean;
  overlayAspectRatio: number;
  isShortBarcodeOverlay?: boolean;
  visibleToRealCaptureWindowHeightRatio: number;
}

const TorchButton = styled.button<{ active: boolean }>`
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;

  svg {
    height: 100%;
    width: 100%;
  }

  ${({ active }) =>
    active &&
    css`
      svg path {
        fill: white;
      }
    `}
`;

const HelpButton = styled.button`
  background: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  height: 40px;

  svg {
    margin-right: 4px;
    height: 16px;
    width: 16px;
  }
`;

const ButtonRowHelp = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;

  button:disabled {
    display: none;
  }

  z-index: ${Z_INDICES.footer};
`;

/**
 * Value that gives a nice level of spacing between viewport and buttons
 * NOTE: default frame height is 650
 */
const VIDEO_WRAPPER_HEIGHT = 720;
const VideoWrapper = styled('div')<{ flowType: FlowType }>`
  background: ${COLORS.black};
  overflow: hidden;
  position: relative;
  min-height: ${FRAME_HEIGHT}px;
  display: flex;
  justify-content: center;
  ${({ flowType }) =>
    // Don't use viewport units for inline flow
    flowType === FlowType.Inline
      ? `
      height: ${VIDEO_WRAPPER_HEIGHT}px;
    `
      : `
      @media ${MEDIA_QUERIES.isMobile} {
        height: 100%;
        min-height: 140vw;
      }
    `};

  ${HolderClient.isDashboardFLE && `height: 100%;`};
`;

const MAX_INLINE_COLUMN_WIDTH = 450;
const VideoWrapperColumnSizer = styled.div<{ restrictInlineColumnWidth: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${({ restrictInlineColumnWidth }) =>
    restrictInlineColumnWidth ? `${MAX_INLINE_COLUMN_WIDTH}px` : null};
`;

const getVideoPosition = ({ isShortBarcodeOverlay }: VideoWrapperProps) => {
  if (isShortBarcodeOverlay) {
    return '35%';
  } else {
    return '25%';
  }
};

const VideoWrapperInner = styled('div')<VideoWrapperProps>`
  background: ${COLORS.black};
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  position: absolute;
  top: ${(props) => getVideoPosition(props)};
  width: 100%;
  // multiply by 0.8 because aspect ratio is inrelation to 80% of the width of screen
  padding-bottom: ${(props) => `${(1 / props.overlayAspectRatio) * 100}%`};
  // How much higher up the real capture window is than the overlay is equal to
  // [real capture window height] * (1 - visibleToRealCaptureWindowHeightRatio) / 2 =
  // [real capture window width] / aspect ratio * (1 - visibleToRealCaptureWindowHeightRatio) / 2 =
  // [wrapper width] / aspect ratio * (1 - visibleToRealCaptureWindowHeightRatio) / 2 =
  // negating the multiplier of wrapper width and multiplying by 100 gives us the desired margin-top percentage
  margin-top: ${(props) =>
    `${
      -(((1 / props.overlayAspectRatio) * (1 - props.visibleToRealCaptureWindowHeightRatio)) / 2) *
      100
    }%`};
  video {
    position: absolute;
    transform: scaleX(${(props) => (props.mirrored ? -1 : 1)});
    object-fit: cover;
    height: 100%;
    width: 100%;
  }
`;

const ButtonsArea = styled.div`
  position: absolute;
  bottom: 40px;
  width: 100%;
  display: flex;
  flex-direction: column;
  z-index: ${Z_INDICES.footer};
`;

const ButtonRowMobile = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ButtonRowDesktop = styled.div`
  margin-left: 10%;
  margin-right: 10%;
  width: 80%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  z-index: ${Z_INDICES.footer};
`;

const AuxButtons = styled.div`
  position: absolute;
  align-items: center;
  display: flex;
  right: 10%;
  line-height: 1;
`;

const CircleCaptureButton = styled.button<{ manualCaptureEnabled: boolean }>`
  background: none;
  cursor: pointer;
  margin-left: auto;
  margin-right: auto;

  circle {
    transition-duration: ${TransitionDuration.Fast};
  }

  visibility: ${({ manualCaptureEnabled }) => (manualCaptureEnabled ? 'visibile' : 'hidden')};

  :hover:not(:disabled) {
    #btn-outer {
      stroke: ${Color.CoreSlate100};
    }
    #btn-inner {
      fill: ${Color.CoreSlate100};
    }
  }

  :active:not(:disabled) {
    #btn-outer {
      stroke: ${Color.CoreSlate300};
    }
    #btn-inner {
      fill: ${Color.CoreSlate300};
    }
  }

  :disabled circle {
    #btn-outer {
      stroke: ${Color.CoreSlate500};
    }
    #btn-inner {
      fill: ${Color.CoreSlate500};
    }
  }
`;

const SwitchCameraButton = styled.button<{ open: boolean }>`
  background: transparent;
  cursor: pointer;
  margin-left: 10px;
  line-height: 1;

  ${({ open }) =>
    open
      ? css`
          path {
            stroke: ${({ theme }) => theme.colorPrimary};
          }
          rect {
            fill-opacity: 1;
          }
        `
      : css`
          :hover:not(:disabled) {
            rect {
              fill-opacity: 0.3;
            }
          }

          :active:not(:disabled) {
            rect {
              fill-opacity: 0.4;
            }
          }
        `}
`;

const DropdownItem = styled(Dropdown.Item)<{ selected: boolean }>`
  font-weight: ${({ selected }) => (selected ? 600 : 500)};
`;

const buttonFullscreenSvg = (
  <svg
    role='img'
    width='62'
    height='62'
    viewBox='0 0 62 62'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle id='btn-outer' cx='31' cy='31' r='29.5' stroke='white' strokeWidth='3' />
    <circle id='btn-inner' cx='31' cy='31' r='26' fill='white' />
  </svg>
);

const buttonThemeOverride = {
  ...defaultTheme,
  colorButtonPrimary: COLORS.white,
  colorFontButtonPrimary: COLORS.black,
  colorButtonSecondary: COLORS.white,
  colorFontButtonSecondary: COLORS.white,
  colorButtonSecondaryFill: COLORS.black,
  borderWidth: '1.5px',
  borderRadius: '24px',
};

const ThemedButton = styled(Button)<{ manualCaptureEnabled: boolean }>`
  visibility: ${({ manualCaptureEnabled }) => (manualCaptureEnabled ? 'visible' : 'hidden')};
`;

const AutoCapturePillPositioner = styled.div`
  position: absolute;
  top: 36px;
  display: flex;
  justify-content: center;
  width: 100%;
  transform: translateY(-50%);

  // should be below navbar
  z-index: ${Z_INDICES.navbar - 1};
`;

interface BaseScannerProps<TriggerType, TriggerParams, Localizations> {
  handleCapture: ({
    imageSrcs,
    captureMethod,
    params,
    cameraInspectionData,
  }: {
    imageSrcs: string[];
    captureMethod: CaptureMethod;
    params: TriggerParams;
    cameraInspectionData: CameraInspectionData;
    clientExtractions: ClientExtraction[];
    clientExtractionSource: string | undefined;
  }) => Promise<void>;
  handleFailed: (err: Error | string, state: 'load' | 'capture') => void;
  detectInterval: number;
  labelTakePhoto: string;
  videoConstraints: MediaTrackConstraints;
  /**
   * WARNING: This is used to initialize the frame trigger client in a ref.
   * Changing this prop at runtime will no-op.
   */
  triggerConfigs: FrameTriggerConfig<TriggerType, TriggerParams, Localizations>[];
  handleTrigger?: (trigger: FrameTrigger<TriggerType, TriggerParams, Localizations>) => void;
  paramsFromVideoFrame: (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    captureClient: LivePhotoCaptureClient | null,
    triggerClient: FrameTriggerClient<TriggerType, TriggerParams, Localizations>,
    viewfinderRegionInCanvas: Rect,
  ) => Promise<TriggerParams>;
  renderOverlay?: (props: ScannerOverlayPassthroughProps) => ReactNode;
  shouldSubmit: (
    params: TriggerParams,
    triggerClient: FrameTriggerClient<TriggerType, TriggerParams, Localizations>,
  ) => boolean;
  /**
   * Callback to determine if quad corner highlights should be displayed.
   */
  hasValidQuad?: (params: TriggerParams) => boolean;
  delayUntilManualCaptureMs?: number;
  triggerOverride?: FrameTrigger<TriggerType, TriggerParams, Localizations>;
  isShortBarcodeOverlay?: boolean;
  showHelpButton?: boolean;
  helpButtonIconColor?: string;
  helpButtonLabel?: string;
  renderHelpModal?: (props: ScannerHelpModalPassthroughProps) => JSX.Element;

  // Currently only DocumentGeneric uses DocumentFileOriginal but eventually all captures should use it.
  captures?: DocumentFileOriginal[];
  // TODO: [PER-8501] add error callback and check for permission denied errors
  scannerType: 'governmentId' | 'microblink' | 'barcode' | 'document';
  handleCameraChange: (selectedCamera: SelectedCamera) => void;
  selectedCamera?: string;
  cameraSelectOptions: SelectedCamera[];
  shouldDisplayCameraSelector: boolean;

  overlayReady: boolean;

  overlayRef: RefObject<HTMLDivElement>;
  overlayCaptureWindowRef: RefObject<HTMLDivElement>;
  previewViewfinderRef: RefObject<HTMLDivElement>;

  submitButtonStyles?: ButtonComponentStyles;

  renderAutoCapturePill?: (props: AutoCapturePillPassthroughProps) => JSX.Element;

  disableCaptureClient?: boolean;
  uiReady: boolean;
  videoReady: boolean;

  assets: StepGovernmentIdConfig['assets']['capture-page'];
  governmentIdClass: GovernmentIdClass;
  requiresSide: GovernmentIdSide;
  captureOverlay?: GovernmentIdOverlay | null;

  captureFrameLabel: string;
  hint?: string | null;
  prompt?: string | null;
}

export default function BaseScanner<TriggerType, TriggerParams, Localizations>(
  props: BaseScannerProps<TriggerType, TriggerParams, Localizations>,
) {
  const isMobile = supportsInputCapture();

  const showCameraErrorDialogOnUnavailableVideoStream = useInquiryFeatureFlag(
    'inquiry:show-camera-error-dialog-on-unavailable-video-stream',
  );

  const useNewPerfLayoutActor = useInquiryFeatureFlag(
    'inquiry:enable-experimental-govid-perf-improvements',
  );

  const useNewPerfLayoutPercentage = useInquiryFeatureFlag(
    'inquiry:enable-experimental-govid-perf-improvements-percentage',
  );

  const useNewPerfLayout = useNewPerfLayoutActor && useNewPerfLayoutPercentage;

  const [t] = useTranslation();

  const {
    base: baseStyles,
    captureFeedBox,
    captureHint,
    captureOverlayHeaderButton,
  } = useStepGovernmentIdStyleContext();

  const timerDetect = useRef(undefined);
  const timerCapture = useRef(undefined);

  // ref to keep track of modal state to prevent autocapture while help
  const helpModalIsOpen = useRef(false);

  const webcam = useRef<Webcam & HTMLVideoElement>();
  const canvasBuffer = useRef<HTMLCanvasElement>();

  useEffect(() => {
    // Ensure this doesn't blow up if we try to access canvasBuffer.current too early.
    const [canvas, cleanup] = createCanvas();
    canvasBuffer.current = canvas;

    if (!props.disableCaptureClient) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      captureClient.current = new LivePhotoCaptureClient({ canvasBuffer: canvasBuffer.current });
    }

    return cleanup;
  }, []);

  const { logCameraInspectionFromStream, logCameraInspectionSnapshot, getCameraInspectionData } =
    useCameraInspection();

  const triggerClient = useRef(
    new FrameTriggerClient(props.triggerConfigs.map((config) => new FrameTrigger(config))),
  );

  const onTrigger = useCallback(
    (trigger: FrameTrigger<TriggerType, TriggerParams, Localizations>) => {
      props.handleTrigger?.(trigger);
    },
    [props.handleTrigger],
  );

  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isCapturing, _setIsCapturing] = useState(false);
  const isCapturingRef = useRef(false);
  // This is awkward, but we store isCapturing as BOTH a state and a ref.
  // This is because state accessed in inline functions use the initial value
  // of the state and never update references.
  //https://github.com/Aminadav/react-useStateRef#motivation
  const setIsCapturing = (isCapturing: boolean) => {
    _setIsCapturing(isCapturing);
    isCapturingRef.current = isCapturing;
  };
  const [hasDetectedQuad, { enable: showDetectedQuad, disable: hideDetectedQuad }] =
    useToggle(false);
  const [debouncedHideDetectedQuad, cancelHideDetectedQuad] = useDebounced(hideDetectedQuad, 500, {
    maxWait: 500,
  });
  const captureClient = useRef<LivePhotoCaptureClient>(null);
  const videoTrack = useRef<MediaStreamTrack>();

  const [initialSelectedCamera, setInitialSelectedCamera] = useState<string | null>(null);
  const [videoTrackSettings, setVideoTrackSettings] = useState<{
    height: number;
    width: number;
    // We don't request exact facing mode in our constraints. The resulting camera facing mode may differ from what was requested.
    // NOTE: Some browsers/devices don't report facingMode for their cameras.
    facingMode: 'user' | 'environment' | string | undefined;
  } | null>(null);

  const [torchLit, { toggle: toggleTorch }] = useToggle();
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [focusModeAvailable, setFocusModeAvailable] = useState(false);
  const [manualCaptureEnabled, setManualCaptureEnabled] = useState(
    props.delayUntilManualCaptureMs === 0 || isInCypress(),
  );
  const isMounted = useIsMounted();

  const [showBarcodeHelpDialog, { enable: openBarcodeHelpDialog, disable: hideBarcodeHelpDialog }] =
    useToggle();

  const selectedCameraFacingMode =
    videoTrackSettings?.facingMode ?? props.videoConstraints.facingMode;

  // refs to enable pausing manual capture timeout while help modal is open
  // and autocapture is disabled
  const manualCaptureTimeout = useRef<number>();
  const manualCaptureTimeoutStart = useRef<number>();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const manualCaptureTimeRemaining = useRef<number>(props.delayUntilManualCaptureMs);

  const { videoDimensionsUpdated } = useTrackVideoDimensions();

  const startManualCaptureTimer = useCallback(() => {
    if (manualCaptureTimeRemaining.current > 0 && !manualCaptureTimeout.current) {
      manualCaptureTimeoutStart.current = Date.now();
      manualCaptureTimeout.current = window.setTimeout(
        () => setManualCaptureEnabled(true),
        manualCaptureTimeRemaining.current,
      );
    }
  }, []);

  const stopManualCaptureTimer = useCallback(() => {
    if (manualCaptureTimeout.current) {
      clearTimeout(manualCaptureTimeout.current);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      manualCaptureTimeRemaining.current -= Date.now() - manualCaptureTimeoutStart.current;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      manualCaptureTimeout.current = null;
    }
  }, []);

  useEffect(() => {
    if (props.overlayReady) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (props.delayUntilManualCaptureMs > 0) {
        startManualCaptureTimer();
      }

      return () => {
        stopManualCaptureTimer();
      };
    }
  }, [props.overlayReady]);

  const openBarcodeHelpDialogAndStopManualCaptureTimeout = useCallback(() => {
    helpModalIsOpen.current = true;
    openBarcodeHelpDialog();
    stopManualCaptureTimer();
  }, [openBarcodeHelpDialog, stopManualCaptureTimer]);

  const hideBarcodeHelpDialogAndStartManualCaptureTimeout = useCallback(() => {
    hideBarcodeHelpDialog();
    startManualCaptureTimer();
    helpModalIsOpen.current = false;
  }, [hideBarcodeHelpDialog, startManualCaptureTimer]);

  // WARNING: Because this function is defined inline and used in a setTimeout,
  // state values accessed in this function will always be stale. Use refs
  // instead.
  const runCaptureClient = () => {
    if (!timerCapture.current) {
      return;
    }

    // Video is null if webcam is unmounting
    // also only run if overlay is mounted.
    if (
      webcam.current?.video != null &&
      webcam.current?.video?.videoWidth > 0 &&
      props.overlayRef.current?.clientWidth != null &&
      props.overlayRef.current.clientWidth > 0 &&
      props.overlayCaptureWindowRef.current?.clientWidth != null &&
      props.overlayCaptureWindowRef.current.clientWidth > 0 &&
      canvasBuffer.current != null &&
      !helpModalIsOpen.current && // don't run trigger client if help modal is open
      !isCapturingRef.current && // not otherwise capturing
      props.uiReady // not transitioning
    ) {
      try {
        // Drawing video here can throw a transient NS_ERROR_NOT_AVAILBLE in some browsers.
        bufferImageWithOverlayWindow({
          source: webcam.current.video,
          canvasBuffer: canvasBuffer.current,
        });
        captureClient.current?.capture();
      } catch (err) {
        // NS_ERROR_NOT_AVAILABLE is thrown when the canvas isn't ready. Just wait in that case.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        if (err.name !== 'NS_ERROR_NOT_AVAILABLE') {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          props.handleFailed(err, 'capture');
        }
      }
    }

    if (timerCapture.current != null) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerCapture.current = window.setTimeout(runCaptureClient, FRAME_INTERVAL);
    }
  };

  const captureAndSubmit = async (captureMethod: CaptureMethod, params: TriggerParams) => {
    if (!props.uiReady || isCapturing) {
      return;
    }

    // disables the submit button. Note that does not disable the back button
    // back button is disabled during the call to handleCapture
    // (which would otherwise invoke the conflicting navigation event)
    // we don't need to prevent the call to paramsFromVideoFrame on back navigation
    // because we already don't do this during trigger client runs and
    // paramsFromVideoFrame should be implemented in such a way that it is safe
    // from canvases / video elements being unmounted
    setIsCapturing(true);

    try {
      if (webcam.current?.video != null && canvasBuffer.current != null) {
        bufferImageWithOverlayWindow({
          source: webcam.current.video,
          canvasBuffer: canvasBuffer.current,
        });

        captureClient.current?.capture();
        if (!params) {
          params = await props.paramsFromVideoFrame(
            webcam.current.video,
            canvasBuffer.current,
            captureClient.current,
            triggerClient.current,
            calculateViewfinderRegionInCanvas(
              webcam.current.video,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              props.overlayCaptureWindowRef.current,
              props.previewViewfinderRef.current,
            ),
          );
        }

        await props.handleCapture({
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          imageSrcs: captureClient.current?.leastBlurryImages(),
          captureMethod,
          params,
          cameraInspectionData: getCameraInspectionData({ includeSmallImages: false }),
        });
      }
    } finally {
      if (isMounted()) {
        setIsCapturing(false);
      }
    }
  };

  // Runs the "tick" for autocapture. A tick is run every DETECT_INTERVAL.
  // However, because the next tick is not scheduled until processing is
  // finished, DETECT_INTERVAL represents the MINIMUM time between ticks. There
  // should never be more than 1 tick being evaluated at once.
  //
  // WARNING: Because this function is defined inline and used in a setTimeout,
  // state values accessed in this function will always be stale. Use refs
  // instead.
  const runTriggerClient = async () => {
    if (!timerDetect.current) {
      return;
    }

    const startTime = new Date().getTime();

    // Video is null if webcam is unmounting
    // also only run if overlay is mounted.
    if (
      (webcam.current?.video?.videoWidth ?? 0) > 0 &&
      (props.overlayRef.current?.clientWidth ?? 0) > 0 &&
      (props.overlayCaptureWindowRef.current?.clientWidth ?? 0) > 0 &&
      canvasBuffer.current != null &&
      !helpModalIsOpen.current && // don't run trigger client if help modal is open
      !isCapturingRef.current && // not otherwise capturing
      props.uiReady // not transitioning
    ) {
      const params = await props.paramsFromVideoFrame(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        webcam.current.video,
        canvasBuffer.current,
        captureClient.current,
        triggerClient.current,
        calculateViewfinderRegionInCanvas(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          webcam.current.video,
          props.overlayCaptureWindowRef.current,
          props.previewViewfinderRef.current,
        ),
      );

      logCameraInspectionSnapshot(canvasBuffer.current);
      videoDimensionsUpdated(webcam.current?.video?.videoWidth, webcam.current?.video?.videoHeight);

      triggerClient.current.runTick(params);
      onTrigger(triggerClient.current.getTriggered());

      // if quad is detected, immediately show indicator
      if (props.hasValidQuad?.(params)) {
        cancelHideDetectedQuad();
        showDetectedQuad();
      } else {
        // if quad is no longer detected, hide indicator on a debounce to avoid jitter
        debouncedHideDetectedQuad();
      }

      // check help modal status again here because generating params could take a while
      if (!helpModalIsOpen.current && props.shouldSubmit(params, triggerClient.current)) {
        captureAndSubmit(CaptureMethod.Auto, params);

        // Stop capture
        return;
      }
    }

    // If the old timeout was cancelled while in this async function, don't run a new tick
    if (timerDetect.current != null) {
      const nextFrameDelay = Math.max(0, props.detectInterval - (new Date().getTime() - startTime));
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerDetect.current = window.setTimeout(runTriggerClient, nextFrameDelay);
    }
  };

  // Cleanup VideoClient on unmount
  useEffect(() => {
    return () => {
      VideoClient.dispose();
    };
  }, []);

  useEffect(() => {
    if (videoTrackSettings && props.videoReady) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerDetect.current = window.setTimeout(runTriggerClient, props.detectInterval);
      if (!props.disableCaptureClient) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        timerCapture.current = window.setTimeout(runCaptureClient, FRAME_INTERVAL);
      }
    }

    return () => {
      clearTimeout(timerDetect.current);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerDetect.current = null;
      clearTimeout(timerCapture.current);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerCapture.current = null;
    };
  }, [videoTrackSettings, props.videoReady]);

  useEffect(() => {
    try {
      // Prevent OperationError: the associated track is in an invalid state
      if (videoTrack.current?.readyState === 'live' && torchAvailable) {
        const constraints: MediaTrackConstraintSet = { torch: torchLit };

        if (focusModeAvailable) {
          // Need to reapply focusMode; does not auto-merge constraint configs.
          constraints.focusMode = 'continuous';
        }

        videoTrack.current.applyConstraints({ advanced: [constraints] });
      }
    } catch (error) {
      captureMessage('Failed to enable torch', 'warning', { error });
    }
  }, [torchLit]);

  // Check for video stream interruptions
  useEffect(() => {
    const loggingInterval = window.setInterval(() => {
      try {
        if (!isMounted()) return;

        const stream = webcam.current?.stream;
        // Webcam may not have mounted yet; rely on stream == null check in setRefWebcam
        if (stream == null) return;
        const track = stream?.getVideoTracks()[0];

        if (!stream.active || track == null || !track?.enabled || track?.readyState !== 'live') {
          const errorMessage = 'Video stream is not available after webcam mount';
          if (showCameraErrorDialogOnUnavailableVideoStream) {
            props.handleFailed(errorMessage, 'load');
          } else {
            captureMessage(errorMessage, 'warning', {
              streamActive: stream.active,
              trackEnabled: track?.enabled,
              trackReadyState: track?.readyState,
              scannerType: props.scannerType,
              ...props.videoConstraints,
            });
          }
          window.clearInterval(loggingInterval);
        }
      } catch (e) {
        captureException(e, 'warning');
      }
    }, 1000);

    return () => {
      window.clearInterval(loggingInterval);
    };
  }, []);

  /**
   * On webcam mount, sets up capture client, logs video metadata, and sets up
   * torch button if available.
   */
  const setRefWebcam = (newRef: Webcam & HTMLVideoElement) => {
    if (newRef) {
      webcam.current = newRef;
      // There is a longstanding React bug where the muted attribute is not
      // propagated to the DOM <video> element. This can cause permissions
      // related issues.
      // Sample fix: https://stackoverflow.com/a/66153842
      // Github issue: https://github.com/facebook/react/issues/6544#issuecomment-705702546
      // Remove if upstream fix merged: https://github.com/mozmorris/react-webcam/issues/332
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.defaultMuted = true;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.muted = true;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.addEventListener('loadedmetadata', async () => {
        // NOTE: do NOT call getUserMedia here, it causes video to fail in iOS Safari
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const stream = webcam.current.stream;

        if (stream == null) {
          const errorMessage = 'Video stream is not available on webcam mount';
          if (showCameraErrorDialogOnUnavailableVideoStream) {
            props.handleFailed(new Error(errorMessage), 'load');
          } else {
            captureMessage(errorMessage, 'warning', {
              scannerType: props.scannerType,
              ...props.videoConstraints,
            });
          }
          return;
        }

        const track = stream.getVideoTracks()[0];
        // PER-21286
        if (track == null) {
          props.handleFailed('Video track is null', 'load');
          return;
        }

        // reset canvas
        // FIXME: not cleaning up this canvas minorly leaks memory. Not a big
        // concern if we are not creating canvases on render / in a loop.
        const [newCanvas, _cleanup] = createCanvas();
        canvasBuffer.current = newCanvas;
        if (!props.disableCaptureClient) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          captureClient.current = new LivePhotoCaptureClient({
            canvasBuffer: canvasBuffer.current,
          });
        }

        const { deviceId, height, width, facingMode } = track.getSettings();

        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setInitialSelectedCamera(deviceId);
        setVideoTrackSettings({
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          height,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          width,
          facingMode,
        });

        // Prevent OperationError: the associated track is in an invalid state
        // If ImageCapture isn't supported, then torch won't be either
        if (track.readyState !== 'live' || typeof ImageCapture === 'undefined') {
          return;
        }

        try {
          // NOTE: must call these together. Torch config in mediaCapabilities
          // is filled when imageCapture.getPhotoCapabilities() is called.
          await new ImageCapture(track).getPhotoCapabilities();

          // The user may have navigated away while getting photo capabilities.
          // Check readyState again.
          if (track.readyState !== 'live') {
            return;
          }

          const mediaCapabilities = track.getCapabilities();

          if (track.applyConstraints.toString().includes('function')) {
            if (mediaCapabilities.focusMode != null) {
              // Enable continuous autofocus if available
              track.applyConstraints({
                advanced: [
                  {
                    focusMode: 'continuous',
                  },
                ],
              });
            }

            videoTrack.current = track;
            setTorchAvailable(mediaCapabilities.torch != null);
            setFocusModeAvailable(mediaCapabilities.focusMode != null);
          }
        } catch (error) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          if (error.name !== 'OperationError') {
            // See PER-5997 OperationError means we couldn't access the track. Do nothing, as torch is optional.
            captureException(error, 'warning');
          }
        }
      });

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.addEventListener('canplay', async () => {
        // Navigating away can cause this event to fire when video is null
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        if (webcam.current.video) {
          setIsCameraReady(true);
          // video autoplay / play permissioning is all over the place.
          // if the webcam is in 'canplay' status but it's paused,
          // let's usher it to play.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          if (webcam.current.video.paused) {
            try {
              // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play
              // Some browsers will prompt for permission on (auto)play.
              // Other browsers will fail immediately (e.g. if the user blocks autoplay in settings).
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              await webcam.current.video.play();
            } catch (e) {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              props.handleFailed(e, 'load');
            }
          }
        }
      });
    }
  };

  const cameraDropdownItems = props.cameraSelectOptions?.map((option, idx) => ({
    id: option.deviceId,
    label: `Camera ${idx + 1}`,
    onClick: () => props.handleCameraChange(option),
  }));

  const overlayAspectRatio =
    props.overlayCaptureWindowRef.current && props.overlayCaptureWindowRef.current?.clientHeight > 0
      ? props.overlayCaptureWindowRef.current.clientWidth /
        props.overlayCaptureWindowRef.current.clientHeight
      : 1.5158097997;

  const helpModal =
    props.showHelpButton &&
    props.renderHelpModal?.({
      isOpen: showBarcodeHelpDialog,
      onClose: hideBarcodeHelpDialogAndStartManualCaptureTimeout,
    });

  return (
    <VideoWrapper
      flowType={HolderClient.flowType}
      data-test={`scanner__${props.scannerType}`}
      data-scanner-type={props.scannerType}
    >
      <VideoWrapperColumnSizer restrictInlineColumnWidth={HolderClient.isInline}>
        <AutoCapturePillPositioner>
          {props.renderAutoCapturePill?.({
            isCameraReady: isCameraReady && props.videoReady,
          })}
        </AutoCapturePillPositioner>
        <VideoWrapperInner
          mirrored={selectedCameraFacingMode === 'user'}
          overlayAspectRatio={overlayAspectRatio}
          isShortBarcodeOverlay={props.isShortBarcodeOverlay}
          visibleToRealCaptureWindowHeightRatio={
            props.isShortBarcodeOverlay
              ? SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO
              : VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO
          }
        >
          {!useNewPerfLayout && props.overlayReady && (
            <Webcam
              audio={false}
              width='100%'
              height='initial'
              ref={setRefWebcam}
              videoConstraints={props.videoConstraints}
              onUserMedia={logCameraInspectionFromStream}
              onUserMediaError={(error) => props.handleFailed(error, 'load')}
            />
          )}
        </VideoWrapperInner>
        {!useNewPerfLayout &&
          props.renderOverlay?.({
            isCameraReady,
            hasDetectedQuad,
            mirrored: selectedCameraFacingMode === 'user',
            renderUnderOverlayComponents: () =>
              !!helpModal && (
                <ButtonRowHelp>
                  <HelpButton
                    id='scanner__button--help'
                    onClick={openBarcodeHelpDialogAndStopManualCaptureTimeout}
                  >
                    <ThemedSvg
                      $primaryColor={props.helpButtonIconColor}
                      $strokeColor={props.helpButtonIconColor}
                    >
                      <CircleInfoIcon aria-hidden />
                    </ThemedSvg>
                    <span>{props.helpButtonLabel}</span>
                  </HelpButton>
                </ButtonRowHelp>
              ),
          })}
        {useNewPerfLayout && (
          <ScannerOverlayNew
            assets={props.assets}
            hasDetectedQuad={hasDetectedQuad}
            styles={{
              base: baseStyles,
              captureBox: captureFeedBox,
              captureOverlayHeaderButton: captureOverlayHeaderButton,
              hint: captureHint?.textStyle,
            }}
            isAnimated={false}
            collapsed={false}
            flipped={false}
            renderCaptureWindowContents={() => (
              <Webcam
                audio={false}
                width='100%'
                height='initial'
                ref={setRefWebcam}
                videoConstraints={props.videoConstraints}
                onUserMedia={logCameraInspectionFromStream}
                onUserMediaError={(error) => props.handleFailed(error, 'load')}
              />
            )}
            renderUnderOverlayComponents={() =>
              !!helpModal && (
                <ButtonRowHelp>
                  <HelpButton
                    id='scanner__button--help'
                    onClick={openBarcodeHelpDialogAndStopManualCaptureTimeout}
                  >
                    <ThemedSvg
                      $primaryColor={props.helpButtonIconColor}
                      $strokeColor={props.helpButtonIconColor}
                    >
                      <CircleInfoIcon aria-hidden />
                    </ThemedSvg>
                    <span>{props.helpButtonLabel}</span>
                  </HelpButton>
                </ButtonRowHelp>
              )
            }
            isShortBarcodeOverlay={props.isShortBarcodeOverlay}
            governmentIdClass={props.governmentIdClass}
            requiresSide={props.requiresSide}
            captureOverlay={props.captureOverlay}
            mirrored={selectedCameraFacingMode === 'user'}
            captureFrameLabel={props.captureFrameLabel}
            prompt={props.videoReady ? props.prompt : t('government-id.capture.loading-camera')}
            hint={props.hint}
            overlayRef={props.overlayRef}
            overlayCaptureWindowRef={props.overlayCaptureWindowRef}
            previewViewfinderRef={props.previewViewfinderRef}
          />
        )}
        <ButtonsArea>
          {isMobile ||
          torchAvailable ||
          (props.shouldDisplayCameraSelector && props.cameraSelectOptions?.length > 1) ? (
            <ButtonRowMobile>
              <CircleCaptureButton
                disabled={
                  !props.uiReady ||
                  isCapturing ||
                  !isCameraReady ||
                  !manualCaptureEnabled ||
                  !props.videoReady
                }
                manualCaptureEnabled={isCameraReady && manualCaptureEnabled}
                id='scanner__button--capture-circle'
                data-test='scanner__button--capture'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onClick={captureAndSubmit.bind(null, CaptureMethod.Manual, null)}
              >
                {buttonFullscreenSvg}
              </CircleCaptureButton>
              <AuxButtons>
                {torchAvailable ? (
                  <TorchButton
                    id='scanner__button--torch'
                    onClick={toggleTorch}
                    active={torchLit}
                    title={torchLit ? 'Flash ON' : 'Flash OFF'}
                  >
                    <IconCameraTorch />
                  </TorchButton>
                ) : (
                  <Spacer height={1} />
                )}

                {props.shouldDisplayCameraSelector && props.cameraSelectOptions?.length > 1 && (
                  <Dropdown
                    items={cameraDropdownItems}
                    placement='top-end'
                    renderItem={(item) => (
                      <DropdownItem
                        id={item.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onClick();
                        }}
                        key={item.id}
                        selected={(props.selectedCamera || initialSelectedCamera) === item.id}
                      >
                        {item.label}
                      </DropdownItem>
                    )}
                  >
                    {({ isOpen, openDropdown, closeDropdown }) => (
                      <OutsideClickHandler onOutsideClick={closeDropdown}>
                        <SwitchCameraButton
                          open={isOpen}
                          id='scanner__button--switch-camera'
                          data-test='scanner__button--switch-camera'
                          onClick={() => openDropdown()}
                          title='Select Camera'
                          aria-label='Select Camera'
                        >
                          <SwitchCamera />
                        </SwitchCameraButton>
                      </OutsideClickHandler>
                    )}
                  </Dropdown>
                )}
              </AuxButtons>
            </ButtonRowMobile>
          ) : (
            <ButtonRowDesktop>
              <ThemedButton
                disabled={
                  !props.uiReady ||
                  isCapturing ||
                  !isCameraReady ||
                  !manualCaptureEnabled ||
                  !props.videoReady
                }
                id='scanner__button--capture-full'
                isPending={!props.uiReady || isCapturing}
                data-test='scanner__button--capture'
                manualCaptureEnabled={isCameraReady && manualCaptureEnabled}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onClick={captureAndSubmit.bind(null, CaptureMethod.Manual, null)}
                theme={buttonThemeOverride}
                styles={props.submitButtonStyles}
              >
                {props.labelTakePhoto}
              </ThemedButton>
            </ButtonRowDesktop>
          )}
        </ButtonsArea>
      </VideoWrapperColumnSizer>
      {helpModal}
    </VideoWrapper>
  );
}
